import React, { useCallback, useState } from 'react';
import { Button, Icon } from '@mercator-gcp/grout';
import { useToggle } from 'react-use';
import { AddCollectionName } from 'components/modals/AddCollectionName';
import FlyoutMenu from 'components/widgets/Dashboard/FlyoutMenu';
import Section from 'components/widgets/Dashboard/FlyoutMenu/Section';
import SectionDropdown from 'components/widgets/Dashboard/FlyoutMenu/SectionDropdown';
import SectionDropdownList from 'components/widgets/Dashboard/FlyoutMenu/SectionDropdownList';
import SectionItem from 'components/widgets/Dashboard/FlyoutMenu/SectionItem';
import SectionItemList from 'components/widgets/Dashboard/FlyoutMenu/SectionItemList';
import { DEFAULT_COLLECTIONS } from 'components/widgets/Dashboard/PinnedProjects/constants';
import { useDefaultCollections } from 'hooks/useDefaultCollections';
import { useHiddenProjects } from 'hooks/useHiddenProjects';
import { useSelectedLeadFilters } from 'hooks/useLeadTableFiltering';
import { useProjectData } from 'hooks/useProjectsByCategory';
import { useDashboardGroupName } from 'state/dashboard';
import { useSelectedCollectionQuery } from 'state/queryParams';
import { CollectionProjectList } from 'types';
import { FilterViewEnum } from 'types/FilterProjects';

type TCollectionListProps = {
  groups: CollectionProjectList[];
};

const CollectionList = ({ groups }: TCollectionListProps) => {
  const { setSelectedLeadFilters: setActiveTabs } = useSelectedLeadFilters();
  const [selectedGroup, setSelectedGroup] = useSelectedCollectionQuery();
  const [, setGroupName] = useDashboardGroupName();
  const [isOpen, setOpen] = useToggle(false);
  const [selectedCategory, setSelectedCategory] = useState<FilterViewEnum>();
  const { getCollectionSummary } = useProjectData();
  const [expandedGroupId, setExpandedGroupId] = useState<number | null>(null);
  const [customCollectionSelected, setCustomCollectionSelected] = useState<number | null>(
    null,
  );

  const { data: collectionSummary } = getCollectionSummary(customCollectionSelected);

  const toggleExpansion = (groupId: number) => {
    if (expandedGroupId === groupId) {
      setExpandedGroupId(null); // collapse if it's the same group
    } else {
      setExpandedGroupId(groupId); // expand the new group
    }
  };

  const { getAllHiddenProjectIdsForUser } = useHiddenProjects();

  const { data: hiddenProjectsSet } = getAllHiddenProjectIdsForUser;

  const { getSavedProjectsByCategory } = useDefaultCollections();

  const { data: defaultCollectionsSummary } = getSavedProjectsByCategory;

  const handleOptionClick = (id: number | string, name: string) => {
    setSelectedGroup?.(String(id));
    setGroupName?.(name);
  };

  const handleSelectDefaultCollection = (id: number | string, name: string) => {
    handleOptionClick(id, name);
  };

  const handleSelectCollection = (id: number) => {
    setCustomCollectionSelected(id);
    setActiveTabs([FilterViewEnum.STAR, FilterViewEnum.WATCH]);
    setSelectedCategory(undefined);
  };

  const handleSelectCollectionCategory = (category: FilterViewEnum, name: string) => {
    setActiveTabs([category]);
    setSelectedCategory(category);
    const isSwitchingBackToCustomCollection =
      String(selectedGroup) !== String(customCollectionSelected);
    if (isSwitchingBackToCustomCollection) {
      handleOptionClick(String(customCollectionSelected), name);
    }
  };

  const onCloseNewCollectionModal = () => {
    setOpen(false);
  };

  const onSaveNewCollectionModal = (newCollectionId: number, name: string) => {
    setOpen(false);
    if (newCollectionId) {
      handleOptionClick(newCollectionId, name);
      handleSelectCollection(newCollectionId);
    }
  };

  const isActive = useCallback(
    (id?: number | string) => String(id) === selectedGroup,
    [selectedGroup],
  );

  const { ALL_HIDDEN, ALL_WATCHED, ALL_STARRED } = DEFAULT_COLLECTIONS;

  return (
    <>
      <FlyoutMenu
        mainAction={
          <Button
            variant="primary"
            onClick={() => setOpen(!isOpen)}
            startAdornment={<Icon name={Icon.Name.Plus} />}
          >
            New lead list
          </Button>
        }
      >
        <Section title="Lead Lists">
          <SectionDropdownList>
            {groups &&
              groups.map(({ id, name, num_projects }) => (
                <SectionDropdown
                  key={`collection-list-section-dropdown-${name}-${id}`}
                  label={name}
                  numProjects={num_projects}
                  isActive={isActive(id)}
                  isExpanded={expandedGroupId === id}
                  onClick={() => {
                    handleOptionClick(id, name);
                    handleSelectCollection(id);
                    toggleExpansion(id);
                  }}
                >
                  <SectionItem
                    label="Qualified"
                    numProjects={Number(collectionSummary?.number_of_star_projects || 0)}
                    iconName={Icon.Name.Star}
                    isActive={selectedCategory === FilterViewEnum.STAR}
                    onClick={() =>
                      handleSelectCollectionCategory(FilterViewEnum.STAR, name)
                    }
                    isSubsection
                  />
                  <SectionItem
                    label="Monitoring"
                    numProjects={Number(collectionSummary?.number_of_watch_projects || 0)}
                    iconName={Icon.Name.Eye}
                    isActive={selectedCategory === FilterViewEnum.WATCH}
                    onClick={() =>
                      handleSelectCollectionCategory(FilterViewEnum.WATCH, name)
                    }
                    isSubsection
                  />
                </SectionDropdown>
              ))}
          </SectionDropdownList>
        </Section>
        <Section title="All lead lists">
          <SectionItemList>
            <SectionItem
              key={ALL_STARRED.key}
              label={ALL_STARRED.label}
              numProjects={defaultCollectionsSummary?.number_of_star_projects}
              isActive={isActive(ALL_STARRED.key)}
              onClick={() => {
                handleSelectDefaultCollection(ALL_STARRED.key, ALL_STARRED.label);
              }}
              iconName={Icon.Name.Star}
            />
            <SectionItem
              key={ALL_WATCHED.key}
              label={ALL_WATCHED.label}
              numProjects={defaultCollectionsSummary?.number_of_watch_projects}
              isActive={isActive(ALL_WATCHED.key)}
              onClick={() => {
                handleSelectDefaultCollection(ALL_WATCHED.key, ALL_WATCHED.label);
              }}
              iconName={Icon.Name.Eye}
            />
            <SectionItem
              key={ALL_HIDDEN.key}
              label={ALL_HIDDEN.label}
              numProjects={hiddenProjectsSet?.size}
              isActive={isActive(ALL_HIDDEN.key)}
              onClick={() => {
                handleSelectDefaultCollection(ALL_HIDDEN.key, ALL_HIDDEN.label);
              }}
              iconName={Icon.Name.EyeHide}
            />
          </SectionItemList>
        </Section>
      </FlyoutMenu>
      <AddCollectionName
        isOpen={isOpen}
        onClose={onCloseNewCollectionModal}
        onSuccess={onSaveNewCollectionModal}
      />
    </>
  );
};

export default CollectionList;
